import { type ReactNode, useRef } from 'react';
import { Image, TriangleAlert } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/AlertDialog/AlertDialog';
import { Button } from '@/components/Button/Button';

export interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: ReactNode;
    description?: ReactNode;
    confirmLabel: ReactNode;
    cancelLabel?: ReactNode;
    /** The action loses something or cannot be undone: the confirm button turns destructive. */
    destructive?: boolean;
    /** While true the dialog stays open, the confirm button spins and nothing can be pressed twice. */
    pending?: boolean;
    onConfirm: () => void;
    /**
     * One control the question needs an answer to before it can be confirmed, between the
     * description and the buttons. Deleting a wardrobe location that still holds pieces asks
     * where they go and deletes it in the same step (ALS-52), which is one dialog and not two.
     * Anything longer than a single field belongs in a drawer, not here.
     */
    children?: ReactNode;
    /**
     * What is about to go, one row each, on a tinted panel: a small icon, the name, and a quiet
     * count on the right (the prototype's confirm-box list, drawn for the Lookbooks frames).
     */
    list?: readonly ConfirmDialogListItem[];
    /** One warning line under it all, before the buttons: "This cannot be undone". */
    note?: ReactNode;
}

export interface ConfirmDialogListItem {
    key: string;
    label: ReactNode;
    value?: ReactNode;
    /** The row's glyph; a picture by default. */
    icon?: ReactNode;
}

/**
 * The question before an action the person would regret undone: log out, delete, cancel an
 * order. One sentence for what happens, one for what it costs, two buttons where the safe
 * one is on the left and the action names itself ("Log out", never "OK"). Focus lands on
 * the safe button, also over a drawer, and goes back where it was once answered; Escape and
 * the scrim are the same as cancelling.
 */
export function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel,
    cancelLabel = 'Cancel',
    destructive = false,
    pending = false,
    onConfirm,
    children,
    list,
    note,
}: ConfirmDialogProps) {
    const safe = useRef<HTMLButtonElement>(null);
    const returnFocus = useRef<HTMLElement | null>(null);
    return (
        <AlertDialog
            open={open}
            onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}
        >
            <AlertDialogContent
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    returnFocus.current =
                        document.activeElement instanceof HTMLElement
                            ? document.activeElement
                            : null;
                    safe.current?.focus({ preventScroll: true });
                }}
                onCloseAutoFocus={(event) => {
                    if (
                        returnFocus.current?.isConnected &&
                        document.activeElement === document.body
                    ) {
                        event.preventDefault();
                        returnFocus.current.focus();
                    }
                }}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    {description ? (
                        <AlertDialogDescription>{description}</AlertDialogDescription>
                    ) : null}
                </AlertDialogHeader>
                {list && list.length > 0 ? (
                    <ul className="bg-muted flex max-h-56 flex-col gap-2 overflow-auto rounded-md p-3">
                        {list.map((item) => (
                            <li key={item.key} className="flex items-center gap-3">
                                <span
                                    aria-hidden
                                    className="bg-card text-muted-foreground flex size-[22px] shrink-0 items-center justify-center rounded-sm [&>svg]:size-[13px]"
                                >
                                    {item.icon ?? <Image />}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-sm">
                                    {item.label}
                                </span>
                                {item.value !== undefined ? (
                                    <span className="text-label-sm text-muted-foreground shrink-0">
                                        {item.value}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                ) : null}
                {children}
                {note ? (
                    <p className="text-warning-muted-foreground flex items-center gap-1.5 text-xs">
                        <TriangleAlert className="size-[13px] shrink-0" aria-hidden />
                        {note}
                    </p>
                ) : null}
                <AlertDialogFooter>
                    {/* The prototype's confirm buttons are the 32px sm size (components.js). */}
                    <Button
                        ref={safe}
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        disabled={pending}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        variant={destructive ? 'destructive' : 'primary'}
                        size="sm"
                        onClick={onConfirm}
                        loading={pending}
                    >
                        {confirmLabel}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
